import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Button, Dropdown, Form, Input, Modal } from 'semantic-ui-react';

import PositionedModal from 'common/components/PositionedModal';
import { ModalBody, ModalActions } from 'common/components/CustomModal';
import { HeaderCell, Table, TableCell, TableHeader, TableRow } from 'common/components/Table';
import { H2, PageSection, Subtitle, Title } from 'common/styles/page';
import { StyledIconButton, StyledButton } from 'common/components/button/StyledButton';
import { getAssessmentPeriods } from 'common/selectors/auth';
import { employeeRecApi } from '../api/employeeRecordApi';
import { EmployeeRecordRead } from 'store/generated/portal/employee-records';
import { format } from 'date-fns';
import { RECORD_TYPES } from '../utils/EmployeeUtils';

interface EmployeeRecordField {
  id: number;
  name: string;
  display_name: string;
  required: boolean;
  type: string;
  location: string;
  valid_values: any[];
}

interface MemberRecordsTabProps {
  employee_id: number;
  employeeRecordFields: { [key: string]: EmployeeRecordField };
  onInputChange: (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
      | { target: { name: string; value: any } }
  ) => void;
}

const MemberEditRecordsTab: React.FC<MemberRecordsTabProps> = ({
  employee_id,
  employeeRecordFields,
  onInputChange,
}) => {
  const [selectedField, setSelectedField] = useState<EmployeeRecordField | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [selectedAssessmentPeriod, setSelectedAssessmentPeriod] = useState<number | null>(null);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const [createRecord] = employeeRecApi.useApiPortalV1EmployeeRecordsCreateMutation();
  const [updateRecord] = employeeRecApi.useApiPortalV1EmployeeRecordsUpdateMutation();
  const [deleteRecord] = employeeRecApi.useApiPortalV1EmployeeRecordsDestroyMutation();

  const { data: employeeRecordValues = [] } = employeeRecApi.useApiPortalV1EmployeeRecordsListQuery(
    { employee: employee_id }
  );

  const assessmentPeriods = useSelector(getAssessmentPeriods) || [];
  const sortedPeriods = [...assessmentPeriods].sort(
    (a, b) => new Date(b.startDateTime).getTime() - new Date(a.startDateTime).getTime()
  );

  const allFields = useMemo(
    () =>
      Object.entries(employeeRecordFields || {}).sort(([, a], [, b]) =>
        (a.display_name || a.name).localeCompare(b.display_name || b.name)
      ),
    [employeeRecordFields]
  );

  const displayByPeriod = useMemo(() => {
    const grouped: { [periodId: number]: Record<string, EmployeeRecordRead> } = {};

    employeeRecordValues.forEach(record => {
      const periodId = Number(record.assessment_period);
      if (!grouped[periodId]) grouped[periodId] = {};
      grouped[periodId][record?.employee_record_value.id] = record;
    });

    return grouped;
  }, [employeeRecordValues]);

  const resetModal = () => {
    setSelectedField(null);
    setNewValue('');
    setSelectedAssessmentPeriod(null);
    setIsModalOpen(false);
  };

  const handleAddValue = (periodId: number, field: EmployeeRecordField) => {
    setModalMode('add');
    setSelectedField(field);
    setSelectedAssessmentPeriod(periodId);
    setNewValue('');
    setIsModalOpen(true);
  };

  const handleEditValue = (periodId: number, field: EmployeeRecordField) => {
    const existing = displayByPeriod[periodId]?.[field.id];
    setModalMode('edit');
    setSelectedField(field);
    setSelectedAssessmentPeriod(periodId);
    setNewValue(existing?.value || '');
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedField || !newValue || !selectedAssessmentPeriod) return;
    const fieldId = selectedField.id;
    const existingRecord = displayByPeriod[selectedAssessmentPeriod]?.[fieldId];

    try {
      if (modalMode === 'add') {
        await createRecord({
          employeeRecordCreateUpdate: {
            assessment_period: selectedAssessmentPeriod,
            employee: employee_id,
            employee_record_value: fieldId,
            value: newValue,
          },
        }).unwrap();
      } else if (existingRecord?.id) {
        await updateRecord({
          id: existingRecord.id,
          employeeRecord: {
            employee: employee_id,
            value: newValue,
          },
        }).unwrap();
      }

      onInputChange({ target: { name: selectedField.name, value: newValue } });
      resetModal();
    } catch (error) {
      console.error('Failed to save employee record:', error);
    }
  };

  if (allFields.length === 0) {
    return (
      <div className="text-center" style={{ padding: '2rem' }}>
        <p>No employee records have been configured.</p>
      </div>
    );
  }

  return (
    <div>
      {sortedPeriods.map(period => (
        <PageSection key={period.id} padbottom="30px">
          <H2 padbottom='0'>{period.name}</H2>
          <Table>
            <TableHeader>
              <TableRow>
                <HeaderCell width={5}>Record</HeaderCell>
                <HeaderCell width={5}>Value</HeaderCell>
                <HeaderCell width={5}>Updated</HeaderCell>
                <HeaderCell width={1}></HeaderCell>
              </TableRow>
            </TableHeader>
            <Table.Body>
              {allFields.map(([fieldId, field]) => {
                const record = displayByPeriod[period.id]?.[parseInt(fieldId)];
                return (
                  <TableRow
                    $selectable
                    key={fieldId}
                    onClick={() =>
                      record ? handleEditValue(period.id, field) : handleAddValue(period.id, field)
                    }
                  >
                    <TableCell $firstcol>{field.display_name || field.name}</TableCell>
                    <TableCell>{record?.value ?? '-'}</TableCell>
                    <TableCell>
                      {record?.updated_at
                        ? format(new Date(record.updated_at), 'MMM dd, yyyy H:mm a')
                        : '-'}
                    </TableCell>
                    <TableCell justify="right" onClick={(e: { stopPropagation: () => any; }) => e.stopPropagation()}>
                      {record ? (
                        <Dropdown
                          button
                          icon="ellipsis vertical"
                          floating
                          className="icon"
                          style={{ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0' }}
                          direction="left"
                        >
                          <Dropdown.Menu>
                            <Dropdown.Item
                              icon="edit"
                              text="Edit"
                              onClick={() => handleEditValue(period.id, field)}
                            />
                            <Dropdown.Item
                              icon="trash"
                              text="Delete"
                              onClick={() => setConfirmDeleteId(record.id)}
                            />
                          </Dropdown.Menu>
                        </Dropdown>
                      ) : (
                        <StyledIconButton
                          icon="plus"
                          title="Add Record"
                          onClick={() => handleAddValue(period.id, field)}
                          color="green"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </Table.Body>
          </Table>
        </PageSection>
      ))}

      <PositionedModal open={isModalOpen} onClose={resetModal} size="small">
        <Modal.Header>{`${selectedField?.name}, ${sortedPeriods.find(p => p.id === selectedAssessmentPeriod)?.name ?? ''}`}</Modal.Header>
        <Modal.Content>
          <ModalBody>
            <Form onSubmit={handleSubmit}>
              <Form.Field>
                <label>{selectedField?.display_name}</label>
                {selectedField?.type === RECORD_TYPES.NUMBER ||
                  selectedField?.type === RECORD_TYPES.DECIMAL ? (
                  <Input
                    type="number"
                    step={selectedField.type === RECORD_TYPES.DECIMAL ? 'any' : '1'}
                    placeholder="Enter a number"
                    value={newValue}
                    onChange={e => setNewValue(e.target.value)}
                  />
                ) : selectedField?.type === RECORD_TYPES.DATE ? (
                  <Input type="date" value={newValue} onChange={e => setNewValue(e.target.value)} />
                ) : (
                  <Input
                    placeholder={`Enter ${(selectedField?.display_name || selectedField?.name || 'value').toLowerCase()}`}
                    value={newValue}
                    onChange={e => setNewValue(e.target.value)}
                  />
                )}
              </Form.Field>
            </Form>
          </ModalBody>
        </Modal.Content>
        <Modal.Actions>
          <ModalActions>
            <Button onClick={resetModal} type="button">
              Cancel
            </Button>
            <Button
              primary
              onClick={handleSubmit}
              disabled={!newValue || !selectedAssessmentPeriod}
              type="button"
            >
              {modalMode === 'edit' ? 'Save Record' : 'Add Record'}
            </Button>
          </ModalActions>
        </Modal.Actions>
      </PositionedModal>

      <PositionedModal open={confirmDeleteId !== null} onClose={() => setConfirmDeleteId(null)} size="small">
        <Modal.Header>Delete Record</Modal.Header>
        <Modal.Content>
          <ModalBody>
            <p>{(() => {
              const rec = (employeeRecordValues || []).find(r => r.id === confirmDeleteId);
              const fieldLabel = rec?.employee_record_value?.name || 'this record';
              const periodName = rec ? (sortedPeriods.find(p => p.id === Number(rec.assessment_period))?.name ?? '') : '';
              return periodName ? `Are you sure you want to delete ${fieldLabel} for ${periodName}?` : `Are you sure you want to delete ${fieldLabel}?`;
            })()}</p>
          </ModalBody>
        </Modal.Content>
        <Modal.Actions>
          <ModalActions>
            <Button onClick={() => setConfirmDeleteId(null)} type="button">
              Cancel
            </Button>
            <Button
              negative
              type="button"
              onClick={async () => {
                try {
                  if (confirmDeleteId) {
                    await deleteRecord({ id: confirmDeleteId }).unwrap();
                    setConfirmDeleteId(null);
                  }
                } catch (err) {
                  console.error('Failed to delete record:', err);
                }
              }}
            >
              Delete
            </Button>
          </ModalActions>
        </Modal.Actions>
      </PositionedModal>
    </div>
  );
};

export default MemberEditRecordsTab;
